/**
 * Interface for a MutationObserver with an optional FeKeyInterceptor property.
 */
interface FeKeyInterceptorMutationObserver extends MutationObserver
{
  feKeyInterceptor?: FeKeyInterceptor;
}

/**
 * Class for intercepting key events on HTML elements.
 */
export default class FeKeyInterceptor
{
  private _dotNetRef: any;
  private _options: any;
  private _element: any;
  private _observer: FeKeyInterceptorMutationObserver;
  private _observedChildren: any[];
  private _keyOptions: any;
  private _regexOptions: any[];
  private _logger: any;
  private _feKeyInterceptor: FeKeyInterceptor;

  /**
   * Creates an instance of FeKeyInterceptor.
   * @param dotNetRef - Reference to the .NET object.
   * @param options - Configuration options for the FeKeyInterceptor.
   */
  constructor (dotNetRef, options)
  {
    this._dotNetRef = dotNetRef;
    this._options = options;
    this._logger = options.enableLogging ? console.log : (message) => { };
    this._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] Interceptor initialized', { options });
  }

  /**
   * Connects the FeKeyInterceptor to an HTML element.
   * @param element - The HTML element to connect to.
   */
  connect (element)
  {
    if (!this._options)
      return;

    if (!this._options.keys)
      throw "_options.keys: array of KeyOptions expected";

    if (!this._options.targetClass)
      throw "_options.targetClass: css class name expected";

    if (this._observer)
    {
      // don't do double registration
      return;
    }

    var targetClass = this._options.targetClass;
    this._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] Start observing DOM of element for changes to child with class ', { element, targetClass });
    this._element = element;
    this._observer = new MutationObserver(this.onDomChanged) as FeKeyInterceptorMutationObserver;
    this._observer.feKeyInterceptor = this;
    this._observer.observe(this._element, { attributes: false, childList: true, subtree: true });
    this._observedChildren = [];
    // transform key options into a key lookup
    this._keyOptions = {};
    this._regexOptions = [];
    for (const keyOption of this._options.keys)
    {
      if (!keyOption || !keyOption.key)
      {
        this._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] got invalid key options: ', keyOption);
        continue;
      }
      this.setKeyOption(keyOption);
    }

    this._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] key options: ', this._keyOptions);
    if (this._regexOptions.length > 0)
      this._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] regex options: ', this._regexOptions);

    // register handlers
    for (const child of this._element.getElementsByClassName(targetClass))
    {
      this.attachHandlers(child);
    }
  }

  /**
   * Sets the key options for the FeKeyInterceptor.
   * @param keyOptions - The key options to set.
   */
  setKeyOption (keyOptions)
  {
    if (keyOptions.key.length > 2 && keyOptions.key.startsWith('/') && keyOptions.key.endsWith('/'))
    {
      // JS regex key options such as "/[a-z]/" or "/a|b/" but NOT "/[a-z]/g" or "/[a-z]/i"
      keyOptions.regex = new RegExp(keyOptions.key.substring(1, keyOptions.key.length - 1)); // strip the / from start and end
      this._regexOptions.push(keyOptions);
    }
    else
      this._keyOptions[ keyOptions.key.toLowerCase() ] = keyOptions;

    // remove whitespace and enforce lowercase
    var whitespace = new RegExp("\\s", "g");
    keyOptions.preventDown = (keyOptions.preventDown || "none").replace(whitespace, "").toLowerCase();
    keyOptions.preventUp = (keyOptions.preventUp || "none").replace(whitespace, "").toLowerCase();
    keyOptions.stopDown = (keyOptions.stopDown || "none").replace(whitespace, "").toLowerCase();
    keyOptions.stopUp = (keyOptions.stopUp || "none").replace(whitespace, "").toLowerCase();
  }

  /**
   * Updates the key options for the FeKeyInterceptor.
   * @param updatedOptions - The updated key options.
   */
  updateKey (updatedOptions)
  {
    var option = this._keyOptions[ updatedOptions.key.toLowerCase() ];
    option || this._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] updating option failed: key not registered');
    this.setKeyOption(updatedOptions);
    this._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] updated option ', { option, updatedOption: updatedOptions });
  }

  /**
   * Disconnects the FeKeyInterceptor from the HTML element.
   */
  disconnect ()
  {
    if (!this._observer)
      return;

    this._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] disconnect mutation observer and event handlers');
    this._observer.disconnect();
    this._observer = null;
    for (const child of this._observedChildren)
      this.detachHandlers(child);
  }

  /**
   * Attaches event handlers to a child element.
   * @param child - The child element to attach handlers to.
   */
  attachHandlers (child)
  {
    this._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] attaching handlers ', { child });
    if (this._observedChildren.indexOf(child) > -1)
    {
      //console.log("... already attached");
      return;
    }
    child.feKeyInterceptor = this;
    child.addEventListener('keydown', this.onKeyDown);
    child.addEventListener('keyup', this.onKeyUp);
    this._observedChildren.push(child);
  }

  /**
   * Detaches event handlers from a child element.
   * @param child - The child element to detach handlers from.
   */
  detachHandlers (child)
  {
    this._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] detaching handlers ', { child });
    child.removeEventListener('keydown', this.onKeyDown);
    child.removeEventListener('keyup', this.onKeyUp);
    this._observedChildren = this._observedChildren.filter(x => x !== child);
  }

  /**
   * Event handler for DOM changes.
   * @param mutationsList The list of mutations that occurred.
   * @param observer The MutationObserver that triggered the event.
   */
  onDomChanged (mutationsList, observer)
  {
    var self = this._feKeyInterceptor; // func is invoked with this == _observer
    //self.logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] onDomChanged: ', { self });
    var targetClass = self._options.targetClass;

    for (const mutation of mutationsList)
    {
      //self.logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] Subtree mutation: ', { mutation });
      for (const element of mutation.addedNodes)
      {
        if (element.classList && element.classList.contains(targetClass))
          self.attachHandlers(element);
      }

      for (const element of mutation.removedNodes)
      {
        if (element.classList && element.classList.contains(targetClass))
          self.detachHandlers(element);
      }
    }
  }

  /**
   * Checks if the key combination matches the given option.
   * @param option - The option to check against.
   * @param args - The key event arguments.
   * @returns True if the key combination matches the option, otherwise false.
   */
  matchesKeyCombination (option, args)
  {
    if (!option || option === "none")
      return false;

    if (option === "any")
      return true;

    var shift = args.shiftKey;
    var ctrl = args.ctrlKey;
    var alt = args.altKey;
    var meta = args.metaKey;
    var any = shift || ctrl || alt || meta;

    if (any && option === "key+any")
      return true;

    if (!any && option.includes("key+none"))
      return true;

    if (!any)
      return false;

    var combi = `key${ shift ? "+shift" : "" }${ ctrl ? "+ctrl" : "" }${ alt ? "+alt" : "" }${ meta ? "+meta" : "" }`;
    return option.includes(combi);
  }

  /**
   * Event handler for key down events.
   * @param args - The key event arguments.
   */
  onKeyDown (args)
  {
    var self = this._feKeyInterceptor; // func is invoked with this == child
    var key = args.key.toLowerCase();
    self._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] down "' + key + '"', args);
    var invoke = false;
    if (self._keyOptions.hasOwnProperty(key))
    {
      var keyOptions = self._keyOptions[ key ];
      self._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] options for "' + key + '"', keyOptions);
      self.processKeyDown(args, keyOptions);
      if (keyOptions.subscribeDown)
        invoke = true;
    }

    for (const keyOptions of self._regexOptions)
    {
      if (keyOptions.regex.test(key))
      {
        self._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] regex options for "' + key + '"', keyOptions);
        self.processKeyDown(args, keyOptions);
        if (keyOptions.subscribeDown)
          invoke = true;
      }
    }

    if (invoke)
    {
      var eventArgs = self.toKeyboardEventArgs(args);
      eventArgs.Type = "keydown";
      self._dotNetRef.invokeMethodAsync('OnKeyDown', self._element.id, eventArgs);
    }
  }

  /**
   * Processes key down events.
   * @param args - The key event arguments.
   * @param keyOptions - The key options to process.
   */
  processKeyDown (args, keyOptions)
  {
    if (this.matchesKeyCombination(keyOptions.preventDown, args))
      args.preventDefault();

    if (this.matchesKeyCombination(keyOptions.stopDown, args))
      args.stopPropagation();
  }

  /**
   * Event handler for key up events.
   * @param args - The key event arguments.
   */
  onKeyUp (args)
  {
    var self = this._feKeyInterceptor; // func is invoked with this == child
    var key = args.key.toLowerCase();
    self._logger('[Nucor.Iron.UI.Js | FeKeyInterceptor] up "' + key + '"', args);
    var invoke = false;

    if (self._keyOptions.hasOwnProperty(key))
    {
      var keyOptions = self._keyOptions[ key ];
      self.processKeyUp(args, keyOptions);
      if (keyOptions.subscribeUp)
        invoke = true;
    }

    for (const keyOptions of self._regexOptions)
    {
      if (keyOptions.regex.test(key))
      {
        self.processKeyUp(args, keyOptions);
        if (keyOptions.subscribeUp)
          invoke = true;
      }
    }

    if (invoke)
    {
      var eventArgs = self.toKeyboardEventArgs(args);
      eventArgs.Type = "keyup";
      self._dotNetRef.invokeMethodAsync('OnKeyUp', self._element.id, eventArgs);
    }
  }

  /**
   * Processes key up events.
   * @param args - The key event arguments.
   * @param keyOptions - The key options to process.
   */
  processKeyUp (args, keyOptions)
  {
    if (this.matchesKeyCombination(keyOptions.preventUp, args))
      args.preventDefault();

    if (this.matchesKeyCombination(keyOptions.stopUp, args))
      args.stopPropagation();
  }

  /**
   * Converts key event arguments to a KeyboardEventArgs object.
   * @param args - The key event arguments.
   * @returns The KeyboardEventArgs object.
   */
  toKeyboardEventArgs (args)
  {
    return {
      Key: args.key,
      Code: args.code,
      Location: args.location,
      Repeat: args.repeat,
      CtrlKey: args.ctrlKey,
      ShiftKey: args.shiftKey,
      AltKey: args.altKey,
      MetaKey: args.metaKey,
      Type: args.type
    };
  }
}